import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import LeadSource from "@/services/lead-source";
import OpportunityStage from "@/services/opportunity/opportunity-stage";
import User from "@/services/user";

export interface OpportunityReferenceDataParams {
  organizationId: string;
}

export function useOpportunityReferenceData(params: Ref<OpportunityReferenceDataParams | null>) {
  const enabled = computed(() => !!params.value?.organizationId);

  const leadSourcesQuery = useQuery({
    queryKey: computed(() => [
      "opportunityLeadSources",
      params.value?.organizationId ?? "disabled",
    ]),
    queryFn: async () => {
      const p = params.value;
      if (!p) throw new Error("Parameters not ready");
      const response = await LeadSource.getLeadSources({
        organizationId: p.organizationId,
      });
      if (response.data?.status && response.data?.data) {
        return response.data.data as Array<{ id: string; name: string }>;
      }
      throw new Error("Failed to load lead sources");
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const stagesQuery = useQuery({
    queryKey: computed(() => ["opportunityStages", params.value?.organizationId ?? "disabled"]),
    queryFn: async () => {
      const p = params.value;
      if (!p) throw new Error("Parameters not ready");
      const response = await OpportunityStage.getOpportunityStages({
        organizationId: p.organizationId,
      });
      if (response.data?.status && response.data?.data) {
        return response.data.data as Array<{ id: string; name: string }>;
      }
      throw new Error("Failed to load opportunity stages");
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const usersQuery = useQuery({
    queryKey: computed(() => ["opportunityUsers", params.value?.organizationId ?? "disabled"]),
    queryFn: async () => {
      const p = params.value;
      if (!p) throw new Error("Parameters not ready");
      const response = await User.getUsers({
        organizationId: p.organizationId,
      });
      if (response.data?.status && response.data?.data) {
        return response.data.data as Array<{
          id: string;
          fullName?: string;
          firstName?: string;
          lastName?: string;
        }>;
      }
      throw new Error("Failed to load users");
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const leadSourceMap = computed(() => {
    const map = new Map<string, string>();
    if (leadSourcesQuery.data.value) {
      leadSourcesQuery.data.value.forEach((source) => {
        map.set(source.name, source.id);
      });
    }
    return map;
  });

  const stageMap = computed(() => {
    const map = new Map<string, string>();
    if (stagesQuery.data.value) {
      stagesQuery.data.value.forEach((stage) => {
        map.set(stage.name, stage.id);
      });
    }
    return map;
  });

  const userMap = computed(() => {
    const map = new Map<string, string>();
    if (usersQuery.data.value) {
      usersQuery.data.value.forEach((user) => {
        const userName = user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim();
        map.set(userName, user.id);
      });
    }
    return map;
  });

  const leadSourceOptions = computed(() => {
    if (!leadSourcesQuery.data.value) return [];
    return leadSourcesQuery.data.value.map((source) => source.name);
  });

  const stageOptions = computed(() => {
    if (!stagesQuery.data.value) return [];
    return stagesQuery.data.value.map((stage) => stage.name);
  });

  const salesRepOptions = computed(() => {
    const base = [{ label: "All Reps", value: "all" }];
    if (!usersQuery.data.value) return base;
    return [
      ...base,
      ...usersQuery.data.value.map((user) => ({
        label: user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        value: user.id,
      })),
    ];
  });

  return {
    leadSourceMap,
    stageMap,
    userMap,
    leadSourceOptions,
    stageOptions,
    salesRepOptions,
  };
}
